import React, { useState } from "react";
import { debuggerService } from "../../services/DebuggerService";
import { BenchmarkResults } from "../../types/debuggerServiceTypes";
import LoadingSpinner from "../LoadingSpinner";
import { getCurrentPSTDateTime } from "../../utils/apiUtils";
import { calculateHitRate } from "../../utils/benchmarkUtils";

interface BatchBenchmarkTestProps {
  prefix: string;
  onBenchmarkResults?: (results: BenchmarkResults) => void;
  numReviewedMessages?: number;
}

const BatchBenchmarkTest: React.FC<BatchBenchmarkTestProps> = ({
  prefix,
  onBenchmarkResults,
  numReviewedMessages = 0,
}) => {
  const [isRunningBatch, setIsRunningBatch] = useState(false);
  const [isLoadingLastResults, setIsLoadingLastResults] = useState(false);
  const [benchmarkResults, setBenchmarkResults] =
    useState<BenchmarkResults | null>(null);
  const [progress, setProgress] = useState<{
    current: number;
    total?: number;
  } | null>(null);

  const getHitRateColor = (hitRate: number): string => {
    if (hitRate >= 90) return "text-green-600";
    if (hitRate >= 75) return "text-yellow-600";
    return "text-red-600";
  };

  const handleBatchCompare = async () => {
    setIsRunningBatch(true);
    setBenchmarkResults(null);
    setProgress({ current: 0 });
    const benchmarkDatetime = getCurrentPSTDateTime();

    try {
      // Use the new chunked method instead of the old single-call method
      const response = await debuggerService.runChunkedBatchBenchmark(
        prefix,
        15,
        (chunkNumber, totalMessages) => {
          setProgress({ current: chunkNumber, total: totalMessages });
        },
        benchmarkDatetime
      );
      setBenchmarkResults(response);
      onBenchmarkResults?.(response);
    } catch (err) {
      console.error("Batch benchmark failed:", err);
      const errorResults = {
        num_previously_matched_now_matched: 0,
        num_previously_matched_now_mismatched: 0,
        num_previously_mismatched_now_matched: 0,
        num_previously_mismatched_now_mismatched: 0,
        num_previously_unconfident_now_confident: 0,
        num_previously_unconfident_now_unconfident: 0,
        num_correct_match_did_not_prune_enough: 0,
        num_correct_match_over_pruned: 0,
        num_correct_prune: 0,
        num_did_not_get_right_tree_or_prune: 0,
        needs_a_new_tree_count: 0,
        total_messages: 0,
      };
      setBenchmarkResults(errorResults);
      onBenchmarkResults?.(errorResults);
    } finally {
      setIsRunningBatch(false);
      setProgress(null);
    }
  };

  const handleGetLastResults = async () => {
    setIsLoadingLastResults(true);
    setBenchmarkResults(null);

    try {
      const response = await debuggerService.getLastBenchmarkResults();
      setBenchmarkResults(response);
      onBenchmarkResults?.(response);
    } catch (err) {
      console.error("Failed to get last benchmark results:", err);
      const errorResults = {
        num_previously_matched_now_matched: 0,
        num_previously_matched_now_mismatched: 0,
        num_previously_mismatched_now_matched: 0,
        num_previously_mismatched_now_mismatched: 0,
        num_previously_unconfident_now_confident: 0,
        num_previously_unconfident_now_unconfident: 0,
        num_correct_match_did_not_prune_enough: 0,
        num_correct_match_over_pruned: 0,
        num_correct_prune: 0,
        num_did_not_get_right_tree_or_prune: 0,
        needs_a_new_tree_count: 0,
        total_messages: 0,
      };
      setBenchmarkResults(errorResults);
      onBenchmarkResults?.(errorResults);
    } finally {
      setIsLoadingLastResults(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 p-1 bg-[#46ACC6]/10 rounded-xl">
      <div className="flex flex-row items-center gap-2 bg-transparent">
        <button
          onClick={handleBatchCompare}
          disabled={isRunningBatch || isLoadingLastResults}
          className="mt-1 inline-flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 disabled:cursor-not-allowed border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm min-w-[140px] bg-transparent"
        >
          {isRunningBatch ? (
            <>
              <LoadingSpinner size="sm" className="mr-2 bg-transparent" />
              <span>
                {progress
                  ? `Chunk ${progress.current + 1}...`
                  : "Running Batch Test..."}
              </span>
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4 mr-2 bg-transparent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              Run Batch Test
            </>
          )}
        </button>
        <button
          onClick={handleGetLastResults}
          disabled={isRunningBatch || isLoadingLastResults}
          className="mt-1 inline-flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 disabled:cursor-not-allowed border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm min-w-[140px] bg-transparent"
        >
          {isLoadingLastResults ? (
            <>
              <LoadingSpinner size="sm" className="mr-2 bg-transparent" />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4 mr-2 bg-transparent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Get Last Results
            </>
          )}
        </button>
      </div>

      {/* Progress Indicator */}
      {isRunningBatch && progress && (
        <div className="mt-2 text-xs text-gray-600 bg-transparent">
          <div className="flex items-center justify-between mb-1">
            <span>Processing chunk {progress.current + 1} - </span>
            {progress.total && <span>{progress.total} messages processed</span>}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-blue-600 h-1 rounded-full transition-all duration-300"
              style={{
                width: `${progress.total && numReviewedMessages ? Math.min((progress.total / numReviewedMessages) * 100, 100) : 0}%`,
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Benchmark Results */}
      {benchmarkResults && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            Benchmark Results
          </h4>

          {/* Hit Rate and Summary in one row */}
          <div className="flex items-center gap-4 mb-2 bg-transparent">
            <div className="flex items-center gap-1 bg-transparent">
              <span className="text-xs font-medium text-gray-700">
                Hit Rate:
              </span>
              <span
                className={`text-sm font-bold ${getHitRateColor(calculateHitRate(benchmarkResults))}`}
              >
                {calculateHitRate(benchmarkResults).toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">Total Compared:</span>
              <span className="text-sm font-medium">
                {benchmarkResults.num_previously_matched_now_matched +
                  benchmarkResults.num_previously_matched_now_mismatched +
                  benchmarkResults.num_previously_mismatched_now_matched +
                  benchmarkResults.num_previously_mismatched_now_mismatched +
                  benchmarkResults.num_previously_unconfident_now_confident +
                  benchmarkResults.num_previously_unconfident_now_unconfident}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">Needs New Tree:</span>
              <span className="text-sm font-medium">
                {benchmarkResults.needs_a_new_tree_count}
              </span>
            </div>
          </div>

          {/* Results in compact grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-green-600">Matched→Matched:</span>
              <span className="font-medium">
                {benchmarkResults.num_previously_matched_now_matched}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-600">Matched→Mismatched:</span>
              <span className="font-medium">
                {benchmarkResults.num_previously_matched_now_mismatched}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Mismatched→Matched:</span>
              <span className="font-medium">
                {benchmarkResults.num_previously_mismatched_now_matched}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-600">Mismatched→Mismatched:</span>
              <span className="font-medium">
                {benchmarkResults.num_previously_mismatched_now_mismatched}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Unconfident→Confident:</span>
              <span className="font-medium">
                {benchmarkResults.num_previously_unconfident_now_confident}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-600">Unconfident→Unconfident:</span>
              <span className="font-medium">
                {benchmarkResults.num_previously_unconfident_now_unconfident}
              </span>
            </div>
            <div className="col-span-2 flex items-center my-1">
              <div className="flex-grow border-t border-gray-200" />
              <span className="mx-2 text-xs text-gray-500 font-medium whitespace-nowrap">
                Pruning Accuracy
              </span>
              <div className="flex-grow border-t border-gray-200" />
            </div>
            <div className="flex justify-between">
              <span className="text-blue-600">Did not prune enough:</span>
              <span className="font-medium">
                {benchmarkResults.num_correct_match_did_not_prune_enough}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-600">Over Pruned:</span>
              <span className="font-medium">
                {benchmarkResults.num_correct_match_over_pruned}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-600">Correct Pruned:</span>
              <span className="font-medium">
                {benchmarkResults.num_correct_prune}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-600">
                Did not get right tree or prune:
              </span>
              <span className="font-medium">
                {benchmarkResults.num_did_not_get_right_tree_or_prune}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchBenchmarkTest;
